import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { SIGN_UP } from "../../graphql/mutations/signUp";
import TextFields from "../../components/TextField/text-field.component";
import Buttons from "../../components/Button/button.component";
import {
  SignUpContainer,
  HeaderContainer,
  NestedModalContainer,
  SignUpTextContainer,
  AccountInformation,
  CreateAccountText,
  FirstInputContainer,
  SecondInputContainer,
  ThirdInputContainer,
  ButtonsContainer,
  BackContainer,
  ErrorContainer,
  AtLeastText,
  Requirement,
  RequirementText,
  SpecialRequirement,
  GoBackText,
  LargeInput,
  Label,
  PhoneBox,
  Container,
  TopContainer,
  CoverContainer,
} from "./PersonalSignUp.styles";
import { colors } from "../../theme/color";
import { emailRegex } from "../../utils/EmailValidation";
import { checkPasswordStrength } from "../../utils/input";
import { useDispatch } from "react-redux";
import { setUserData } from "../../store/user";
import "react-phone-number-input/style.css";
import { verifyNewUser } from "../../graphql/mutations/otp";
import { router } from "../../utils/general";

const SignUp = () => {
  const dispatch = useDispatch();
  const [register, { loading }] = useMutation(SIGN_UP);
  const [verifyUser, { loading: verifyUserLoading }] =
    useMutation(verifyNewUser);
  const navigate = useNavigate();
  const [data, setData] = useState({
    fullName: "",
    businessName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [smallInputForm, setSmallInputForm] = useState("");

  const [phone, setPhone] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: any }>({});

  React.useEffect(() => {
    const passwordStrength = checkPasswordStrength(data.password);
    setErrors({
      ...errors,
      ...passwordStrength,
      ...passwordStrength?.criteriaResults,
    });
  }, [data]);
  const handleNameChange = (key: string, value: string) => {
    setData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
    setIsValidEmail(emailRegex.test(value));
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
  };

  const isFormValid = () => {
    return (
      data.fullName !== "" &&
      data.businessName !== "" &&
      data.email !== "" &&
      phone !== "" &&
      data.password !== "" &&
      data.confirmPassword !== "" &&
      data.confirmPassword === data.password
    );
  };

  const isFormEmpty = () => {
    return smallInputForm === "";
  };

  const NavigateToTypeOfAccount = () => {
    navigate("/auth/type-of-account");
  };

  const NavigateToOtp = () => {
    navigate("/otp");
  };

  const toHomePage: () => void = () => {
    navigate("/");
  };

  const NavigateToDashboard = () => {
    router("/dashboard/home");
  };

  const handleLogin = async () => {
    try {
      const response = await register({
        variables: {
          input: {
            email: data.email,
            phone,
            fullName: data.fullName,
            organizationName: data.businessName,
            password: data.password,
            confirmPassword: data.confirmPassword,
          },
        },
      });
      if (response.data.register.message === "Proceed to verify your account") {
        const { id, organizationId } = response.data.register;

        // Save user data to local storage or state for use in OTP page
        dispatch(setUserData({ organizationId, id, email: data.email }));

        // Navigate to OTP page
        NavigateToOtp();
      } else {
        setErrors({
          others: "Verification failed. Please try again.",
        });
      }
    } catch (e: any) {
      const error = e;
      error.message = error?.message.toString()?.toLowerCase();
      console.error("Error during signup:", error);
      if (error.message.includes("email")) {
        setErrors({ email: error?.message || "Invalid email address" });
      } else if (
        error.message.includes(
          "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one special character, and one number."
        )
      ) {
        setErrors({
          password:
            "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one special character, and one number.",
        });
      } else if (error.message.includes("Confirm password does not match")) {
        setErrors({ confirmPassword: "Passwords do not match" });
      } else {
        setErrors({
          others: error?.message || "An error occurred during signup",
        });
      }
    }
  };
  return (
    <Container>
      <CoverContainer>
        <TopContainer>
          <HeaderContainer>
            <img onClick={toHomePage} src="/assets/icons/fipaylogo.png" />
          </HeaderContainer>
          <BackContainer>
            <img src="/assets/svg/goBack.svg" />
            <GoBackText onClick={NavigateToTypeOfAccount}>Go back</GoBackText>
          </BackContainer>
        </TopContainer>
      </CoverContainer>
      <SignUpContainer>
        <NestedModalContainer>
          <SignUpTextContainer>
            <AccountInformation>
              Provide the account information below
            </AccountInformation>
            {/* <AccountInformation> below</AccountInformation> */}
            <CreateAccountText>
              Fill in the details to create an account
            </CreateAccountText>
          </SignUpTextContainer>
          <FirstInputContainer>
            <TextFields
              type="text"
              placeholder="Jane Doe"
              label="Fullname"
              value={data.fullName}
              onChange={(value: string) => handleNameChange("fullName", value)}
            />
            <TextFields
              type="text"
              placeholder="Quantum Incorporation"
              label="Business name"
              value={data.businessName}
              onChange={(value: string) =>
                handleNameChange("businessName", value)
              }
            />
          </FirstInputContainer>
          <SecondInputContainer>
            <TextFields
              type="email"
              placeholder="example@company.com"
              label="Email Address"
              value={data.email}
              onChange={(value: string) => handleNameChange("email", value)}
            />
            <PhoneBox>
              <Label htmlFor="countryCode">Phone Number</Label>
              <LargeInput
                type="tel"
                onChange={handlePhoneChange}
                value={phone}
                defaultCountry="US"
                maxLength={14}
              />
            </PhoneBox>
          </SecondInputContainer>
          {errors && (
            <p
              style={{
                color: "red",
                paddingRight: "3rem",
                margin: 0,
                fontSize: "0.8rem",
              }}
            >
              {errors.email}
            </p>
          )}

          <ThirdInputContainer>
            <TextFields
              type="password"
              placeholder="●●●●●●●●"
              label="Password"
              value={data.password}
              onChange={(value: string) => handleNameChange("password", value)}
            />
            <TextFields
              type="password"
              placeholder="●●●●●●●●"
              label="Confirm Password"
              value={data.confirmPassword}
              onChange={(value: string) =>
                handleNameChange("confirmPassword", value)
              }
              autoComplete="off"
            />
            {errors.password && (
              <p
                style={{
                  color: "red",
                  padding: 0,
                  margin: 0,
                  fontSize: "0.8rem",
                }}
              >
                {errors.password}
              </p>
            )}
            {errors.confirmPassword && (
              <p
                style={{
                  color: "red",
                  padding: 0,
                  margin: 0,
                  fontSize: "0.8rem",
                }}
              >
                {errors.confirmPassword}
              </p>
            )}
          </ThirdInputContainer>

          <ErrorContainer>
            <AtLeastText>AT LEAST :</AtLeastText>
            <Requirement>
              <RequirementText $active={errors.length}>
                8 characters
              </RequirementText>
              <RequirementText $active={errors.uppercase}>
                An uppercase letter
              </RequirementText>
              <RequirementText $active={errors.lowercase}>
                A lowercase letter
              </RequirementText>
            </Requirement>
            <SpecialRequirement>
              <RequirementText $active={errors.special}>
                A special character
              </RequirementText>
              <RequirementText $active={errors.numeric}>
                A number
              </RequirementText>
            </SpecialRequirement>
          </ErrorContainer>
          {errors && (
            <p style={{ color: "red", fontSize: "0.8rem" }}>{errors.others}</p>
          )}
          <ButtonsContainer>
            <Buttons
              handleClick={handleLogin}
              label={
                loading || verifyUserLoading
                  ? "Creating Account..."
                  : "Continue"
              }
              width="45%"
              color={colors.ButtonBackgroundColors.primaryMediumHoverButton}
              cursor="pointer"
              border="30px"
              height="40px"
              disabled={
                !isFormValid() || loading || verifyUserLoading || !errors?.pass
              }
            />
          </ButtonsContainer>
        </NestedModalContainer>
      </SignUpContainer>
    </Container>
  );
};

export default SignUp;
